import { Copy, ImageUp, Upload } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BottomAction from '@/domains/mobile-web/components/BottomAction/BottomAction';
import BottomSheetMobile, {
  type SheetOption,
  SheetOptionList,
} from '@/domains/mobile-web/components/BottomSheetMobile/BottomSheetMobile';
import CardRefundOrder, {
  RefundNote,
} from '@/domains/mobile-web/components/CardRefundOrder/CardRefundOrder';
import InputMobile from '@/domains/mobile-web/components/InputMobile/InputMobile';
import PickerFieldMobile from '@/domains/mobile-web/components/PickerFieldMobile/PickerFieldMobile';
import TopBarHeader from '@/domains/mobile-web/components/TopBarHeader/TopBarHeader';
import {
  REFUND_COURIER_OPTIONS,
  REFUND_NOTES,
  REFUND_PRODUCT_LEBAH,
  REFUND_STATIC,
} from '@/domains/mobile-web/constant/refund.constant';
import WrapperMobile from '@/shared/components/layout/WrapperMobile/WrapperMobile';
import Button from '@/shared/components/ui/Button';
import HeaderMobile from '@/shared/components/ui/HeaderMobile/HeaderMobile';

const REFUND_RETURN_ADDRESS = {
  label: 'Rumah Kebijaksanaan',
  fullAddress:
    'Jl. Bunga Melati No. 45, RT 004/RW 006, Kelurahan Pakulaya, Kecamatan Serpong Utara, Kota Tangerang Selatan, Banten 15321.',
};

export const REFUND_RETURN_INSTRUCTIONS = [
  'Kemas buku dengan aman.',
  'Kirim menggunakan kurir yang tersedia.',
  'Masukkan nomor resi setelah barang dikirim.',
];

const RefundStatusDetails = () => {
  const navigate = useNavigate();

  const [isCourierSheetOpen, setIsCourierSheetOpen] = useState(false);
  const [courierSearch, setCourierSearch] = useState('');
  const [courier, setCourier] = useState<SheetOption | null>(null);
  const filteredCouriers = REFUND_COURIER_OPTIONS.filter((item) =>
    item.label.toLowerCase().includes(courierSearch.toLowerCase())
  );
  const handleSelectCourier = (option: SheetOption) => {
    setCourier(option);
    setIsCourierSheetOpen(false);
  };

  const [trackingNumber, setTrackingNumber] = useState('');
  const [addressCopied, setAddressCopied] = useState(false);

  const handleCopyAddress = async () => {
    try {
      await navigator.clipboard?.writeText(
        `${REFUND_RETURN_ADDRESS.label}\n${REFUND_RETURN_ADDRESS.fullAddress}`
      );
    } catch {}
    setAddressCopied(true);
    window.setTimeout(() => setAddressCopied(false), 1500);
  };

  const canConfirm = courier !== null && trackingNumber.trim() !== '';

  return (
    <main className="min-h-screen">
      <HeaderMobile>
        <TopBarHeader type="back" onBack={() => navigate(-1)} title="Pengembalian Barang" />
      </HeaderMobile>

      <WrapperMobile className="mb-[100px]">
        <CardRefundOrder
          date={REFUND_STATIC.date}
          status="approved"
          code={REFUND_STATIC.refundCode}
          products={[REFUND_PRODUCT_LEBAH]}
        />
        <article className="shadowCard flex w-full flex-col gap-3 bg-white p-3">
          <h3 className="title2medium text-black">Alamat Pengembalian</h3>
          <div className="flex flex-col gap-1">
            <p className="body1medium text-black">{REFUND_RETURN_ADDRESS.label}</p>
            <p className="reg12 text-text-grey">{REFUND_RETURN_ADDRESS.fullAddress}</p>
          </div>
          <Button
            type="button"
            onClick={handleCopyAddress}
            customButton="h-10! w-full rounded-[8px] text-[14px]"
            className="border border-blue-cs-200 bg-white text-black hover:bg-[#F3F8FF]"
          >
            <Copy size={14} strokeWidth={1.8} />
            {addressCopied ? 'Tersalin' : 'Salin Alamat'}
          </Button>
        </article>

        <article className="shadowCard flex w-full flex-col gap-3 bg-white p-3">
          <h3 className="title2medium text-black">Instruksi Pengembalian</h3>
          <ol className="list-decimal flex flex-col gap-2 pl-5 body1 text-black">
            {REFUND_RETURN_INSTRUCTIONS.map((text) => (
              <li key={text}>{text}</li>
            ))}
          </ol>
        </article>

        <article className="shadowCard flex w-full flex-col gap-3 bg-white p-3">
          <h3 className="title2medium text-black">Detail Pengiriman</h3>
          <PickerFieldMobile
            label="Kurir"
            name="courier"
            placeholder="Pilih Kurir"
            value={courier?.label}
            onClick={() => setIsCourierSheetOpen(true)}
          />
          <InputMobile
            label="Nomor Resi"
            name="trackingNumber"
            required
            placeholder="Masukkan nomor resi pengiriman"
            value={trackingNumber}
            onChange={(e) => setTrackingNumber(e.target.value)}
          />
          <RefundNote text={REFUND_NOTES.shippingCost} />
        </article>

        <article className="shadowCard flex w-full flex-col gap-3 bg-white p-3">
          <h3 className="title2medium text-black">Bukti Pengiriman</h3>
          <div className="flex items-center gap-3">
            <div className="flex h-[80px] w-[96px] shrink-0 items-center justify-center rounded-[12px] border border-dashed border-blue-cs-200 bg-[#F3F8FF] text-text-blue-100">
              <ImageUp size={24} strokeWidth={1.8} />
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-2">
              <p className="reg12 text-text-grey">
                Unggah foto atau video sebagai bukti pengiriman.
              </p>
              <Button
                type="button"
                customButton="h-9! w-fit rounded-[8px] px-3 text-[14px]"
                className="bg-text-blue-100 text-white hover:bg-text-blue-100/90"
              >
                <Upload size={14} strokeWidth={2} />
                Unggah
              </Button>
            </div>
          </div>
        </article>

        <RefundNote text={REFUND_NOTES.returnProcess} />
      </WrapperMobile>

      <BottomAction textButton="Konfirmasi Pengembalian" disabled={!canConfirm} />

      <BottomSheetMobile
        open={isCourierSheetOpen}
        onClose={() => setIsCourierSheetOpen(false)}
        title="Pilih Kurir"
        searchable
        searchValue={courierSearch}
        onSearchChange={setCourierSearch}
        searchPlaceholder="Cari nama kurir"
      >
        <SheetOptionList
          options={filteredCouriers}
          value={courier?.value}
          onSelect={handleSelectCourier}
        />
      </BottomSheetMobile>
    </main>
  );
};

export default RefundStatusDetails;
